import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { alternatives, alternativesHubFaqs } from '../data/alternatives';
import { useSEO } from '../hooks/useSEO';
import { useJsonLd } from '../hooks/useJsonLd';
import {
  buildBreadcrumbSchema,
  buildFaqPageSchema,
  buildItemListSchema,
  buildWebPageSchema,
} from '../utils/seoStructuredData';
import FooterSection from '../components/landing/FooterSection';

const AlternativesPage: React.FC = () => {
  const description =
    'Compare Retrosive to EasyRetro, Parabol, Ludi (Metro Retro), Miro, FigJam, Mural, Retrium, Lucidspark, Microsoft Whiteboard, and TeamRetro. Honest agile retrospective tool comparisons with feature tables and FAQs.';

  useSEO({
    title: 'Best Retrospective Tool Alternatives — Compare Retrosive',
    description,
    canonical: '/alternatives',
    keywords: [
      'retrospective tool comparison',
      'EasyRetro alternative',
      'Parabol alternative',
      'Miro retrospective alternative',
      'Ludi alternative',
      'best retro tool 2026',
      'agile retrospective software',
    ],
  });

  const jsonLd = useMemo(
    () => [
      buildWebPageSchema({
        path: '/alternatives',
        name: 'Retrosive vs Alternatives — Retrospective Tool Comparisons',
        description,
      }),
      buildBreadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Alternatives', path: '/alternatives' },
      ]),
      buildItemListSchema(
        alternatives.map((a) => ({
          name: `Retrosive vs ${a.name}`,
          path: `/alternatives/${a.slug}`,
          description: a.hubSummary,
        }))
      ),
      buildFaqPageSchema(alternativesHubFaqs),
    ],
    [description]
  );

  useJsonLd(jsonLd);

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-slate-900 via-orange-800 to-slate-900 text-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <nav aria-label="Breadcrumb" className="text-sm text-orange-200/80 mb-6">
            <ol className="flex justify-center gap-2 flex-wrap">
              <li>
                <Link to="/" className="hover:text-white">
                  Home
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li className="text-white font-medium">Alternatives</li>
            </ol>
          </nav>
          <span className="inline-block px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm font-medium mb-4">
            Compare Tools
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">
            Retrosive vs the Alternatives
          </h1>
          <p className="text-lg text-orange-100/90 max-w-2xl mx-auto mb-8">
            Not sure which retrospective tool fits your team? See how Retrosive stacks up against
            the most popular agile and whiteboard platforms — with honest feature comparisons.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              className="px-6 py-3 bg-white text-orange-700 font-semibold rounded-xl hover:bg-orange-50 transition-colors"
            >
              Start Free
            </Link>
            <Link
              to="/features"
              className="px-6 py-3 bg-white/10 border border-white/25 font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              See Features
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="mb-10 p-6 bg-orange-50 border border-orange-100 rounded-2xl">
            <h2 className="text-xl font-bold text-slate-900 mb-3">
              The retrospective platform built for agile teams
            </h2>
            <p className="text-gray-700 text-sm leading-relaxed mb-4">
              Retrosive is purpose-built for sprint retrospectives — not general whiteboards adapted
              for agile. Every feature supports facilitators: private writing, anonymous feedback,
              real-time collaboration, action items, Jira integration, and AI summaries that track
              patterns across sprints.
            </p>
            <ul className="grid sm:grid-cols-2 gap-2 text-sm text-gray-700">
              {[
                'AI retrospective summaries in one click',
                'Cross-sprint pattern & sentiment tracking',
                '12+ retro templates, zero setup',
                'Jira sprint context inside the board',
                'Join via link — no account required',
                'Generous free plan — no credit card',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-orange-500 shrink-0">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-6">Compare Retrosive to…</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {alternatives.map((alt) => (
              <Link
                key={alt.slug}
                to={`/alternatives/${alt.slug}`}
                className="group block p-5 border border-gray-200 rounded-xl hover:border-orange-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-2xl" aria-hidden>
                    {alt.emoji}
                  </span>
                  <div>
                    <h3 className="font-semibold text-slate-900 group-hover:text-orange-600 transition-colors">
                      Retrosive vs {alt.name}
                    </h3>
                    <p className="text-xs text-orange-600 font-medium mt-0.5">{alt.hubPitch}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{alt.hubSummary}</p>
                <span className="inline-block mt-4 text-sm font-medium text-orange-600 group-hover:underline">
                  Read comparison →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-slate-50 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Frequently asked questions</h2>
          <div className="space-y-6">
            {alternativesHubFaqs.map((faq) => (
              <article key={faq.question} className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.question}</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">When to choose Retrosive</h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            Retrosive is the right choice when your team runs regular sprint retrospectives and wants
            a tool designed for those sessions — not a general whiteboard with retro templates bolted
            on. If retro actions get lost and summaries take too long to write, Retrosive brings
            structure, engagement, and AI to keep ceremonies effective.
          </p>
          <Link
            to="/register"
            className="inline-block px-8 py-3 bg-orange-600 hover:bg-orange-500 text-white font-semibold rounded-xl transition-colors"
          >
            Try Retrosive Free
          </Link>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default AlternativesPage;
